import type { ResearchDelivery } from './delivery';

export interface ResearchControlCopy {
  title: string;
  explanation: string;
  /** For an owner-default account: consent was recorded in person, so no per-page ask. */
  explanationDefault: string;
  neverCollected: string;
  agree: string;
  join: string;
  stop: string;
  joining: string;
  stopping: string;
  stopped: string;
  unavailable: string;
  statusActive: string;
  generationAgree: string;
  generationEnable: string;
  generationDisable: string;
  generationActive: string;
  generationUnavailable: string;
}

/** Explicit first-party consent control. It is hidden from accounts outside the configured pilot. */
export function mountResearchControls(parent: HTMLElement, delivery: ResearchDelivery, copy: ResearchControlCopy): () => void {
  const section = document.createElement('section');
  section.className = 'research-controls';
  section.setAttribute('aria-labelledby', 'research-controls-title');
  section.hidden = true;
  const title = document.createElement('h2');
  title.id = 'research-controls-title';
  title.textContent = copy.title;
  const explanation = document.createElement('p');
  explanation.textContent = copy.explanation;
  const neverCollected = document.createElement('p');
  neverCollected.textContent = copy.neverCollected;
  const status = document.createElement('p');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const label = document.createElement('label');
  const agree = document.createElement('input');
  agree.type = 'checkbox';
  label.append(agree, document.createTextNode(copy.agree));
  const action = document.createElement('button');
  action.type = 'button';
  let notice = '';
  const generationLabel = document.createElement('label');
  const generationAgree = document.createElement('input');
  generationAgree.type = 'checkbox';
  generationLabel.append(generationAgree, document.createTextNode(copy.generationAgree));
  const generationStatus = document.createElement('p');
  generationStatus.setAttribute('role', 'status');
  generationStatus.setAttribute('aria-live', 'polite');
  const generationAction = document.createElement('button');
  generationAction.type = 'button';
  const render = () => {
    const state = delivery.status;
    const agreedInPerson = state.consentSource === 'owner-default' && state.active;
    explanation.textContent = agreedInPerson ? copy.explanationDefault : copy.explanation;
    explanation.setAttribute('data-consent', agreedInPerson ? 'owner-default' : 'ask');
    if (state.deletionUnconfirmed) {
      const menu = parent.closest('details.overflow-menu') as HTMLDetailsElement | null;
      if (menu) menu.open = true;
    }
    section.hidden = !state.eligible && !state.canOptOut && !state.deletionUnconfirmed;
    label.hidden = !state.eligible || state.active;
    const stopping = state.active || !state.eligible;
    action.hidden = !state.eligible && !state.canOptOut;
    action.disabled = stopping ? false : !agree.checked;
    action.textContent = stopping ? copy.stop : copy.join;
    if (state.deletionUnconfirmed) status.textContent = copy.stopped;
    else if (state.active) status.textContent = copy.statusActive;
    else status.textContent = notice;
    // An active flag under an older wording is not live consent: keep the
    // checkbox visible so the learner re-agrees to the current wider scope.
    const generationLive = !!state.generationContentActive && (state.generationContentRevision ?? 0) >= (state.generationContentCurrentRevision ?? 0);
    generationLabel.hidden = !state.active || generationLive;
    generationAction.hidden = !state.active;
    generationAction.disabled = generationLive ? false : !generationAgree.checked;
    generationAction.textContent = generationLive ? copy.generationDisable : copy.generationEnable;
    generationStatus.textContent = state.active && generationLive ? copy.generationActive : '';
  };
  const onAction = async () => {
    const stopping = delivery.status.active || !delivery.status.eligible;
    action.disabled = true;
    action.textContent = stopping ? copy.stopping : copy.joining;
    const ok = stopping ? await delivery.optOut() : await delivery.optIn();
    notice = ok || stopping ? '' : copy.unavailable;
    agree.checked = false;
    render();
  };
  const onGenerationAction = async () => {
    const state = delivery.status;
    const live = !!state.generationContentActive && (state.generationContentRevision ?? 0) >= (state.generationContentCurrentRevision ?? 0);
    const enabled = !live;
    generationAction.disabled = true;
    const ok = await delivery.setGenerationContentAccess(enabled);
    generationAgree.checked = false;
    render();
    if (!ok) generationStatus.textContent = copy.generationUnavailable;
  };
  agree.addEventListener('change', render);
  generationAgree.addEventListener('change', render);
  action.addEventListener('click', onAction);
  generationAction.addEventListener('click', onGenerationAction);
  window.addEventListener('li-research-state', render);
  section.append(title, explanation, neverCollected, status, label, action, generationLabel, generationAction, generationStatus);
  parent.append(section);
  void delivery.start().then(render);
  render();
  return () => {
    agree.removeEventListener('change', render);
    generationAgree.removeEventListener('change', render);
    action.removeEventListener('click', onAction);
    generationAction.removeEventListener('click', onGenerationAction);
    window.removeEventListener('li-research-state', render);
    section.remove();
  };
}
